import type React from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Table from '../../../../../../components/Table/Table'
import {
  type AdminWealthManagerDto,
  useGetWealthManagersQuery,
} from '../../../../../../redux/services/coreApi'
import { setVeliWealthManagersTableState } from '../../../../../../redux/slices/tableFiltersSlice'
import type { RootState } from '../../../../../../redux/store'
import { RiskRatingBadge, WmStatusBadge } from './WealthManagerChips'

type WmRow = AdminWealthManagerDto & {
  name: string
}

// The wealth-manager roster. A row click drills into that WM's detail sub-page (mirroring the Clients
// table), where the lifecycle actions — invite, mark agreement signed, set risk rating — now live in the
// page header rather than as per-row menu bullets.
const WealthManagersTab: React.FC = () => {
  const history = useHistory()
  const { data, isLoading } = useGetWealthManagersQuery()

  const tableState = useSelector(
    (state: RootState) => state.veliWealthManagersTable
  )

  const displayName = (wm: AdminWealthManagerDto) =>
    [wm.firstName, wm.lastName].filter(Boolean).join(' ') || wm.email

  const goToDetail = (id: string) =>
    history.push(`/app/admin/veli-migration/${id}`)

  const rows: WmRow[] =
    data?.wealthManagers.map(wm => ({
      ...wm,
      name: displayName(wm),
    })) ?? []

  return (
    <Table<WmRow>
      tableState={tableState}
      setTableState={setVeliWealthManagersTableState}
      navigate={() => null}
      isLoading={isLoading}
      onClickRow={row => {
        if (!row) return
        goToDetail(row.id)
      }}
      emptyDataRender={
        <div className="text-xl mb-3">No wealth managers found</div>
      }
      rowMenuActions={[
        {
          title: 'See details',
          hidden: () => false,
          onClick: row => goToDetail(row.id),
        },
      ]}
      defaultData={rows}
      schema={[
        {
          cardTitle: true,
          header: 'Name',
          accessorKey: 'name',
          type: 'string',
          priority: 1,
        },
        {
          header: 'Email',
          accessorKey: 'email',
          type: 'string',
          priority: 2,
        },
        {
          header: 'Phone',
          accessorKey: 'phone',
          type: 'string',
          priority: 5,
        },
        {
          header: 'Status',
          accessorKey: 'status',
          type: 'status',
          priority: 3,
          cellValueWrapper: (_value, _originalValue, { original }) => (
            <WmStatusBadge
              status={original.status}
              isInvited={original.isInvited}
            />
          ),
        },
        {
          header: 'Risk rating',
          accessorKey: 'riskRating',
          type: 'status',
          priority: 4,
          cellValueWrapper: (_value, _originalValue, { original }) => (
            <RiskRatingBadge riskRating={original.riskRating} />
          ),
        },
        {
          header: 'Clients',
          accessorKey: 'clientCount',
          type: 'number',
          priority: 6,
        },
      ]}
    />
  )
}

export default WealthManagersTab
